import { useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Application, Container } from 'pixi.js';

interface GameCanvasProps {
  width?: number;
  height?: number;
  backgroundColor?: number;
  onInit?: (app: Application) => void;
  onResize?: (width: number, height: number) => void;
  className?: string;
}

export interface GameCanvasHandle {
  app: Application | null;
  stage: Container | null;
  resize: (width: number, height: number) => void;
}

export const GameCanvas = forwardRef<GameCanvasHandle, GameCanvasProps>(
  ({ width, height, backgroundColor = 0x1099bb, onInit, onResize, className = '' }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const appRef = useRef<Application | null>(null);

    const resize = useCallback((newWidth: number, newHeight: number) => {
      const app = appRef.current;
      if (!app) return;

      app.renderer.resize(newWidth, newHeight);
      if (onResize) {
        onResize(newWidth, newHeight);
      }
    }, [onResize]);

    useImperativeHandle(ref, () => ({
      app: appRef.current,
      stage: appRef.current?.stage || null,
      resize,
    }), [resize]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      let app: Application;

      const initApp = async () => {
        // Determine initial size
        const initialWidth = width || container.clientWidth || 400;
        const initialHeight = height || container.clientHeight || 600;

        app = new Application();

        await app.init({
          width: initialWidth,
          height: initialHeight,
          backgroundColor,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
          antialias: true,
        });

        appRef.current = app;

        // Add canvas to container
        container.appendChild(app.canvas);

        // Handle resize
        const handleResize = () => {
          if (!width || !height) {
            const newWidth = container.clientWidth;
            const newHeight = container.clientHeight;
            if (newWidth > 0 && newHeight > 0) {
              resize(newWidth, newHeight);
            }
          }
        };

        window.addEventListener('resize', handleResize);

        // Initial callback
        if (onInit) {
          onInit(app);
        }

        return () => {
          window.removeEventListener('resize', handleResize);
        };
      };

      const cleanup = initApp();

      return () => {
        cleanup.then(cleanupFn => cleanupFn?.());
        if (appRef.current) {
          appRef.current.destroy(true, { children: true });
          appRef.current = null;
        }
      };
    }, [width, height, backgroundColor, onInit, resize]);

    return (
      <div
        ref={containerRef}
        className={`w-full h-full overflow-hidden touch-none ${className}`}
        style={{ touchAction: 'none' }}
      />
    );
  }
);

GameCanvas.displayName = 'GameCanvas';
